import { FormEvent, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { classNames, formatCLP } from '../lib/format';
import { useDocumentMeta } from '../lib/seo';
import type { Giftcard, Order } from '../types';

const TOKEN_KEY = 'runmore_customer_token';
const EMAIL_KEY = 'runmore_customer_email';

export function AccountPage({ navigate }: { navigate: (path: string) => void }) {
  useDocumentMeta({ title: 'Mi cuenta · Run More Chile', noindex: true });
  const [token, setToken] = useState(window.localStorage.getItem(TOKEN_KEY) || '');
  const [email, setEmail] = useState(window.localStorage.getItem(EMAIL_KEY) || '');
  const [orderNumber, setOrderNumber] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [giftcards, setGiftcards] = useState<Giftcard[]>([]);
  const [profile, setProfile] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<'pedidos' | 'giftcards' | 'perfil' | 'wishlist'>('pedidos');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!token) return;
    setLoading(true);
    try {
      const [o, g, p] = await Promise.all([api.customer.orders(), api.customer.giftcards(), api.customer.profile()]);
      setOrders(o);
      setGiftcards(g);
      setProfile(p.profile);
      setEmail(p.email);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sesión expirada');
      window.localStorage.removeItem(TOKEN_KEY);
      setToken('');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (token) load();
  }, [token]);

  async function login(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.customer.login(email, orderNumber);
      window.localStorage.setItem(TOKEN_KEY, res.token);
      window.localStorage.setItem(EMAIL_KEY, res.email);
      setToken(res.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo entrar');
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(EMAIL_KEY);
    setToken('');
    setOrders([]);
    setGiftcards([]);
  }

  if (!token) {
    return (
      <main className="static-page">
        <button className="back-button" onClick={() => navigate('/')}>Volver a tienda</button>
        <header className="static-page-header">
          <p className="eyebrow">Mi cuenta</p>
          <h1>Entra a tu cuenta</h1>
          <p className="static-page-intro">Sin contraseñas. Usa el email con que compraste y el número de cualquiera de tus pedidos para entrar.</p>
        </header>

        <form className="account-login" onSubmit={login}>
          <label className="field">
            <span>Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.cl" required />
          </label>
          <label className="field">
            <span>Número de cualquier pedido tuyo</span>
            <input type="number" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="Ej: 1015" required />
          </label>
          {error && <p className="form-message tone-error">{error}</p>}
          <button className="primary-button" disabled={loading}>{loading ? 'Entrando...' : 'Entrar a mi cuenta'}</button>
          <p className="muted small">¿No recuerdas el número de pedido? Búscalo en el email de confirmación que enviamos al hacer la compra.</p>
        </form>
      </main>
    );
  }

  const wishlistIds: string[] = (() => {
    try { return JSON.parse(window.localStorage.getItem('runmore_wishlist') || '[]'); }
    catch { return []; }
  })();

  return (
    <main className="static-page account-page">
      <button className="back-button" onClick={() => navigate('/')}>Volver a tienda</button>
      <header className="account-header">
        <div>
          <p className="eyebrow">Mi cuenta</p>
          <h1>{profile.customer_name || email}</h1>
          <p className="muted">{email}</p>
        </div>
        <button type="button" className="ghost-button compact" onClick={logout}>Cerrar sesión</button>
      </header>

      <nav className="account-tabs">
        {([['pedidos', `Pedidos (${orders.length})`], ['giftcards', `Giftcards (${giftcards.length})`], ['wishlist', `Favoritos (${wishlistIds.length})`], ['perfil', 'Perfil']] as const).map(([id, label]) => (
          <button key={id} type="button" className={classNames('account-tab', tab === id && 'active')} onClick={() => setTab(id as typeof tab)}>{label}</button>
        ))}
      </nav>

      {loading && <p className="muted">Cargando...</p>}

      {tab === 'pedidos' && (
        <section>
          {orders.length === 0 ? (
            <div className="empty-state subtle"><p>Aún no tienes pedidos.</p></div>
          ) : (
            <div className="account-orders">
              {orders.map((o) => (
                <article className="account-order" key={o.id}>
                  <header>
                    <div>
                      <strong>Pedido #{o.orderNumber}</strong>
                      <small>{new Date(o.createdAt).toLocaleDateString('es-CL')}</small>
                    </div>
                    <span className={`order-pill pill-${o.status}`}>{o.status}</span>
                  </header>
                  <ul>
                    {(o.items || []).map((item) => (
                      <li key={item.id}>
                        <span>{item.name} {[item.color, item.size].filter(Boolean).length > 0 && <small>({[item.color, item.size].filter(Boolean).join(' / ')})</small>}</span>
                        <span>x{item.quantity}</span>
                        <span>{formatCLP(item.total)}</span>
                      </li>
                    ))}
                  </ul>
                  <footer>
                    <span>Total: <strong>{formatCLP(o.total)}</strong></span>
                    {o.trackingNumber ? (
                      <span>Tracking: <strong>{o.trackingNumber}</strong></span>
                    ) : (
                      <button type="button" className="link-button" onClick={() => navigate(`/seguimiento?order=${o.orderNumber}&email=${encodeURIComponent(email)}`)}>Ver detalle</button>
                    )}
                  </footer>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === 'giftcards' && (
        <section>
          {giftcards.length === 0 ? (
            <div className="empty-state subtle"><p>No tienes giftcards activas a tu nombre.</p></div>
          ) : (
            <div className="account-giftcards">
              {giftcards.map((g) => (
                <article className="account-giftcard" key={g.id}>
                  <p className="eyebrow">Giftcard</p>
                  <h3>{formatCLP(g.balance)} <small>de {formatCLP(g.initialAmount)}</small></h3>
                  <code>{g.code}</code>
                  {g.message && <p className="account-giftcard-msg">"{g.message}"</p>}
                  {g.senderName && <small className="muted">De parte de {g.senderName}</small>}
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === 'wishlist' && (
        <section>
          {wishlistIds.length === 0 ? (
            <div className="empty-state subtle"><p>Aún no tienes favoritos.</p><p className="muted">Toca el corazón en cualquier producto para guardarlo.</p></div>
          ) : (
            <p className="muted">Tienes {wishlistIds.length} producto(s) marcados como favoritos. Verlos en la tienda manteniendo activado el filtro de favoritos próximamente.</p>
          )}
        </section>
      )}

      {tab === 'perfil' && (
        <section className="account-profile">
          <dl className="settings-list">
            <div><dt>Email</dt><dd>{email}</dd></div>
            <div><dt>Nombre</dt><dd>{profile.customer_name || '—'}</dd></div>
            <div><dt>Teléfono</dt><dd>{profile.customer_phone || '—'}</dd></div>
            <div><dt>RUT</dt><dd>{profile.customer_rut || '—'}</dd></div>
            <div><dt>Última dirección</dt><dd>{profile.shipping_address ? `${profile.shipping_address}, ${profile.shipping_city || ''}` : '—'}</dd></div>
          </dl>
          <p className="muted small">Estos datos se actualizan automáticamente con cada nuevo pedido.</p>
        </section>
      )}
    </main>
  );
}
